import React, { useState, MouseEvent, useMemo, useEffect, useCallback } from 'react';
import styles from './lessonsAccordionBySection.module.scss';
import { Collapse } from 'antd';
import { UpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Content, Lesson, LessonStatus } from '../../../../types';
import { useCourseContent } from '../../../../hooks/useCourseContent';
import { LastVisitedLessonContentFragment as LastVisitedLesson, Section, SectionFragment } from 'src/api/graphql-codegen';
import SectionContents from './section-contents/SectionContents';
import { useAnalytics, Events, useUser } from '@hooks';
import Button from '@src/components/button/Button';
import Tooltip from 'antd/es/tooltip';

const { Panel } = Collapse;

type Props = {
  title: string;
  contents: Content[];
  lesson: Lesson;
  lastVisitedLesson?: LastVisitedLesson;
  trackEventName?: string;
  closeNav?: () => void;
  sections: SectionFragment[];
  startLesson: (lesson: Lesson) => void;
  startExercise?: (exercise: Section, isFromPreview?: boolean) => void;
};

const PanelTitle = ({ title, isSection }: { title: string; isSection?: boolean }) => {
  return (
    <div>
      <div className={styles.panelTitle}>{title}</div>
      <div className={styles.panelSubTitle}>{isSection ? '(5 - 10 mins each)' : '(30 mins +)'}</div>
    </div>
  );
};

const ExtraInfoIcon = ({ tooltipTitle }: { tooltipTitle: string }) => {
  return (
    <div className={styles.extraInfoIconContainer}>
      <Tooltip
        placement='top'
        title={<div className={styles.extraInfoIconTooltip}>
          {tooltipTitle}
        </div>}
        arrowPointAtCenter
        color='#FFFFFF'>
        <InfoCircleOutlined className={styles.extraInfoIcon} />
      </Tooltip>
    </div>
  )
};

const TooltipDisabledButton = ({ tooltipTitle, sectionName }: { tooltipTitle: string, sectionName: string }) => {
  return (
    <Tooltip
      placement='bottom'
      title={<div className={styles.extraInfoIconTooltip}>
        {tooltipTitle}
      </div>}
      destroyTooltipOnHide
      arrowPointAtCenter
      mouseLeaveDelay={0}
      color='#FFFFFF'>
        <div className={styles.visibleOnHoverOnly}>
          <Button disabled label={`Start ${sectionName}`} color='white' size='small' className={styles.buttonDisable} />
        </div>
    </Tooltip>
  )
};

function LessonsAccordionBySections(props: Props) {
  const { title, contents = [], lesson, lastVisitedLesson, trackEventName, closeNav, sections, startLesson, startExercise } = props;

  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const [opened, setOpened] = useState(false);
  const { content: selectedContent, lesson: selectedLesson, program, isParentApp } = useCourseContent();

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const _opened = !opened;
    setOpened(_opened);
    if (_opened && !isParentApp) {
      const _d = {
        title,
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(selectedLesson),
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };
      trackEvent(trackEventName || '', _d);
    }
  };

  useEffect(() => {
    if (lastVisitedLesson && lastVisitedLesson?.lessonId === lesson.id) {
      setOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastVisitedLesson]);

  useEffect(() => {
    if (selectedContent && contents.map(c => c.id).includes(selectedContent.id)) {
      setOpened(true);
    }
  }, [selectedLesson, lesson, selectedContent, contents]);

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    if (contents.find(c => c.id === selectedContent?.id) && selectedLesson?.id === lesson.id) {
      classes.push(styles.selected);
    }

    return classes.join(' ');
  }, [opened, selectedContent, contents, lesson, selectedLesson]);

  const handleStartLesson = useCallback(() => {
    startLesson(lesson)
  }, [startLesson, lesson]);

  const handleStartExercise = useCallback((section: Section) => () => {
    startExercise?.(section, true)
  }, [startExercise]);

  const isIntroductionCompleted = useMemo(() => {
    const introSection = sections.find(section => section.name.toLowerCase() === "introduction");
    return introSection ? introSection.status === LessonStatus.Completed : true;
  }, [sections]);

  return (
    <div className={wrapperClassName}>
      <div onClick={handleToggle} className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div className={styles.subtitle}>{'Preview Lesson as:'}</div>
      </div>
      <div className={styles.content}>
        <Collapse
          bordered={false}
          defaultActiveKey={['1', '2']}
          expandIconPosition="end"
          expandIcon={({ isActive }) => <UpOutlined style={{ fill: '#FFF', color: '#FFF', strokeWidth: '3px', width: 20, fontSize: '20px' }} rotate={isActive ? 180 : 0} />}
          style={{ backgroundColor: 'transparent' }}
        >
          <Panel header={<PanelTitle title="One Longer Class" />} key="1" className={styles.panelStyle} extra={<ExtraInfoIcon tooltipTitle='Select this option if you have 30 minutes or longer for the Lesson.' />} >
            <div className={styles.contentsContainer}>
              <div className={styles.smallTitle}>{title}</div>
              <SectionContents contents={contents} lesson={lesson} lastVisitedLesson={lastVisitedLesson} closeNav={closeNav}/>
              <div className={styles.buttonContainer}>
                <Button label={'Start Lesson'} color='white' size='small' className={styles.viewLesson} onClick={handleStartLesson} />
              </div>
            </div>
          </Panel>
          <Panel header={<PanelTitle title="Multiple Short Classes" isSection />} key="2" className={styles.panelStyle} extra={<ExtraInfoIcon tooltipTitle='Select this option if you have less than 20 minutes for the Lesson.' />} >
            {sections.map((section, ind) => {
              const hasNoContent = !section.modules?.length;
              if (hasNoContent) return null;
              const isIntroduction = section.name === 'Introduction';
              const buttonDisabled = !isIntroductionCompleted && !isIntroduction;
              return (
                <div className={`${styles.contentsContainer}${isIntroduction ? ` ${styles.introContentContainer}` : ''}${buttonDisabled ? ` ${styles.disabledContainer}` : ''}`} key={`${lesson.id}_${section.id}`}>
                  <div className={styles.smallTitle}>{section.name || ''}</div>
                  <SectionContents contents={section.modules || []} lesson={section} lastVisitedLesson={lastVisitedLesson} closeNav={closeNav} />
                  <div className={`${styles.buttonContainer} ${styles.visibleOnHoverOnly}`}>
                    {buttonDisabled ? (
                      <TooltipDisabledButton tooltipTitle='Please complete the required Introduction section first' sectionName={section.name} />
                    ) : (
                      <Button label={`Start ${section.name}`} color='white' size='small' className={styles.sectionViewLesson} onClick={handleStartExercise(section)} />
                    )}
                  </div>
                </div>
              )
            })}
          </Panel>
        </Collapse>
      </div>
    </div>
  );
}

export default React.memo(LessonsAccordionBySections);
